import {
  createLinkedDocumentationItem,
  hasLinkedDocumentationReference,
} from 'shared/domain/issue/linkedDocumentation';
import { requestProjectApi } from 'serviceWorker/api/requestApi';
import { captureDataScope, DataOwner, type DataScope } from 'serviceWorker/helpers/dataScope';
import * as issuesDb from 'serviceWorker/db/issues/issues';
import { getOneByRemote as getDocumentationByRemote } from 'serviceWorker/db/documentations/documentations';
import {
  announceLinkedDocumentationChange,
  appendLinkedDocumentationToIssueRecord,
} from '../model';
import type {
  ImportFromDocumentationRequest,
  ImportFromDocumentationResultData,
} from 'shared/domain/issue/linkedDocumentation/operations';

function importFromDocumentationRequest(
  issueId: string,
  payload: ImportFromDocumentationRequest['payload']
): Promise<void> {
  return requestProjectApi({ method: 'POST', path: `issue/${issueId}/importFromDocumentation`, body: payload });
}

async function appendLinkedDocumentationToIssue(
  issueId: string,
  payload: ImportFromDocumentationRequest['payload']
): Promise<void> {
  const issue = await issuesDb.getOne(issueId);
  if (!issue) {
    return;
  }

  const current = issue.linkedDocumentation ?? [];
  const alreadyExists = hasLinkedDocumentationReference(
    current,
    payload.documentationId,
    payload.versionId
  );

  if (alreadyExists) {
    return;
  }

  const documentation = await getDocumentationByRemote(payload.documentationId);
  const matchedVersion = documentation?.versions?.find(
    (version) =>
      version._id === payload.versionId ||
      version.remoteVersionId === payload.versionId
  );

  await appendLinkedDocumentationToIssueRecord(
    issueId,
    createLinkedDocumentationItem({
      documentationId: payload.documentationId,
      versionId: payload.versionId,
      nameCandidates: [documentation?.name],
      imageRotationCandidates: [matchedVersion?.imageRotation],
    })
  );
}

/** Links the documentation version to the issue on the server and in IndexedDB; rejects when the server refused. */
export async function importFromDocumentation(
  detail: ImportFromDocumentationRequest
): Promise<ImportFromDocumentationResultData> {
  const { issueId, payload } = detail;
  const scope = captureDataScope({ owner: DataOwner.PROJECT });
  await importFromDocumentationRequest(issueId, payload);
  await scope.write(async () => {
    await appendLinkedDocumentationToIssue(issueId, payload);
    await announceLinkedDocumentationChange(issueId);
  });
  return { documentationId: payload.documentationId, versionId: payload.versionId };
}
